import type { OrderStatusCountsData } from "@shared/schemas";
import { OrderStatusCountsSchema } from "@shared/schemas";
import type * as T from "@shared/types";
import { axios } from ".";

export const ORDERS_BASE_URL = "/api/v1/orders/bc-orders";
const BATCHES_BASE_URL = "/api/v1/batches/bc-batches";

const ORDER_STATUS_COUNTS_URL = `${ORDERS_BASE_URL}/status-counts` as const;

export const getInstallationOrder = async (installationId: string, orderId: string) => {
  const url = `/api/v1/orders/vendor-system-orders/installations/${installationId}/orders/${orderId}`;
  const response = await axios.get<{ data: T.Order }>(url);
  return response.data.data;
};

export const getPurchaseOrders = async (params: T.FetchOrdersRequest, signal?: AbortSignal) => {
  // Convert arrays to comma-separated strings for Express 5 compatibility
  const serializedParams: Record<string, unknown> = {
    ...params,
  };

  // Handle orderStatus: convert array to comma-separated string, omit if empty array
  if (Array.isArray(params.orderStatus)) {
    if (params.orderStatus.length > 0) {
      serializedParams.orderStatus = params.orderStatus.join(",");
    } else {
      delete serializedParams.orderStatus;
    }
  }

  // Handle customerAccountNumbers: convert array to comma-separated string, omit if empty array
  if (Array.isArray(params.customerAccountNumbers)) {
    if (params.customerAccountNumbers.length > 0) {
      serializedParams.customerAccountNumbers = params.customerAccountNumbers.join(",");
    } else {
      delete serializedParams.customerAccountNumbers;
    }
  }

  const config: { params: Record<string, unknown>; signal?: AbortSignal } = { params: serializedParams };
  if (signal) {
    config.signal = signal;
  }

  const response = await axios.get<T.PurchaseOrderResponse>(ORDERS_BASE_URL, config);
  return response.data;
};

export const getPurchaseOrderById = async (params: T.FetchPurchaseOrderRequest) => {
  const { purchaseOrderId } = params;
  const url = `${ORDERS_BASE_URL}/${purchaseOrderId}`;
  const response = await axios.get<{ data: T.PurchaseOrder }>(url);
  return response.data;
};

export const createPurchaseOrderShippingDocuments = async (params: Omit<T.FetchPurchaseOrderShippingDocumentRequest, "type">) => {
  const { purchaseOrderId, batchId } = params;
  const url = batchId ? `${BATCHES_BASE_URL}/shipping-documents` : `${ORDERS_BASE_URL}/${purchaseOrderId}/shipping-documents`;

  const requestBody = batchId ? { batchId } : undefined;
  const response = await axios.post<{ data: string }>(url, requestBody);
  return response.data;
};

export const getPurchaseOrderShippingDocument = async (params: T.FetchPurchaseOrderShippingDocumentRequest) => {
  const { purchaseOrderId, batchId, type } = params;
  const url = batchId
    ? `${BATCHES_BASE_URL}/${batchId}/${purchaseOrderId}/shipping-document`
    : `${ORDERS_BASE_URL}/${purchaseOrderId}/shipping-document`;
  const response = await axios.get<{ data: string }>(url, { params: { type } });
  return response.data;
};

export type CrossBorderOrdersParams = Pick<T.FetchCrossBorderOrdersRequest, "direction" | "orderStatus" | "createdFrom" | "createdTo">;

export const getCrossBorderOrders = async (params: CrossBorderOrdersParams, signal?: AbortSignal) => {
  const serializedParams: Record<string, unknown> = { ...params };

  if (Array.isArray(params.orderStatus) && params.orderStatus.length > 0) {
    serializedParams.orderStatus = params.orderStatus.join(",");
  } else {
    delete serializedParams.orderStatus;
  }

  const config: { params: Record<string, unknown>; signal?: AbortSignal } = { params: serializedParams };
  if (signal) {
    config.signal = signal;
  }

  const response = await axios.get<{ data: T.CrossBorderOrderSummary }>(`${ORDERS_BASE_URL}/cross-border`, config);
  return response.data.data;
};

export const getBulkShippingLabelUrls = async (orderIds: { purchaseOrderId: string; batchId?: string }[], signal?: AbortSignal) => {
  const response = await axios.post<{ data: Record<string, string | null> }>(
    `${ORDERS_BASE_URL}/shipping-documents`,
    {
      orders: orderIds,
      type: "Label",
    },
    { signal }
  );
  return response.data.data; // Returns { purchaseOrderId: url | null } mapping
};

export const getOrderStatusCounts = async (vendorKey: string, customerAccountNumbers: string[]): Promise<OrderStatusCountsData> => {
  const params: Record<string, unknown> = { vendorKey };
  if (customerAccountNumbers.length > 0) {
    params.customerAccountNumbers = customerAccountNumbers.join(",");
  }
  const response = await axios.get<{ data: unknown }>(ORDER_STATUS_COUNTS_URL, { params });
  return OrderStatusCountsSchema.parse(response.data.data);
};
